/**
 * Modèle et règles UI du dossier technique logement.
 * Aligné sur le back ({@code DocumentTechniqueType}, {@code LogementDocumentTechniqueDto},
 * {@code DocumentTechniqueReglesService}).
 */

import { isParking, isParkingChauffe, toDateIso } from './logement.model';

export const DOCUMENT_TECHNIQUE_TYPES = [
  'DPE',
  'ELEC',
  'GAZ',
  'PLOMB',
  'AMIANTE',
  'RISQUES',
  'BRUIT',
  'NOTICE',
] as const;

export type DocumentTechniqueType = (typeof DOCUMENT_TECHNIQUE_TYPES)[number];

export const DOCUMENT_TECHNIQUE_RESULTAT = {
  PRESENCE: 'PRESENCE',
  ABSENCE: 'ABSENCE',
} as const;

export type DocumentTechniqueResultat =
  (typeof DOCUMENT_TECHNIQUE_RESULTAT)[keyof typeof DOCUMENT_TECHNIQUE_RESULTAT];

/** Types pour lesquels présence / absence est obligatoire à l'upload. */
export function requiresPresenceAbsence(
  type: DocumentTechniqueType | null | undefined
): boolean {
  return type === 'PLOMB' || type === 'AMIANTE';
}

/** Document technique actif (réponse API). */
export class LogementDocumentTechnique {
  idDocumentDto?: string;
  idLogementDto?: string;
  typeDto!: DocumentTechniqueType;
  resultatDto?: DocumentTechniqueResultat | null;
  objectKeyDto?: string;
  fileNameDto?: string;
  contentTypeDto?: string;
  sizeBytesDto?: number;
  uploadedAtDto?: Date | string;
  dateDiagnosticDto?: Date | string;
  dateExpirationDto?: Date | string | null;
  actifDto?: boolean;
}

/** Corps PUT expiration ({@code null} = sans expiration). */
export interface DocumentTechniqueExpiration {
  dateExpirationDto: string | null;
}

/** Paramètres d'upload multipart (hors fichier binaire). */
export interface DocumentTechniqueUploadParams {
  type: DocumentTechniqueType;
  dateDiagnostic: string;
  resultat?: DocumentTechniqueResultat | null;
  file: File;
}

export type DocumentTechniqueStatut =
  | 'manquant'
  | 'a_jour'
  | 'expire'
  | 'illimite'
  | 'non_applicable';

/** Seuils métier (alignés {@code DocumentTechniqueReglesService}). */
export const DOCUMENT_TECHNIQUE_SEUIL_AGE_ELEC_GAZ_ANS = 15;
export const DOCUMENT_TECHNIQUE_SEUIL_ANNEE_PLOMB = 1949;
/** Amiante parking : avant le 1er juillet 1997 (année ≤ 1997 par précaution). */
export const DOCUMENT_TECHNIQUE_SEUIL_ANNEE_AMIANTE_PARKING = 1997;

export type DocumentTechniqueNonApplicableReason =
  | 'elecGazTropRecent'
  | 'plombTropRecent'
  | 'parking'
  | 'amianteParkingTropRecent';

/** Ligne d'affichage : un type réglementaire + document actif éventuel. */
export interface DocumentTechniqueLigne {
  type: DocumentTechniqueType;
  document?: LogementDocumentTechnique;
  statut: DocumentTechniqueStatut;
  exigible: boolean;
  nonApplicableReason?: DocumentTechniqueNonApplicableReason;
}

/**
 * True si le type est exigible selon l'année, le type d'unité et le chauffage.
 * Parking : amiante (si ≤ 1997 / année inconnue) + risques + DPE si chauffé.
 */
export function isDocumentTechniqueExigible(
  type: DocumentTechniqueType,
  anneeConstruction?: number | null,
  typeUnite?: string | null,
  chauffage?: string | null
): boolean {
  if (isParking(typeUnite)) {
    switch (type) {
      case 'RISQUES':
        return true;
      case 'DPE':
        return isParkingChauffe(chauffage);
      case 'AMIANTE':
        return (
          anneeConstruction == null ||
          anneeConstruction <= DOCUMENT_TECHNIQUE_SEUIL_ANNEE_AMIANTE_PARKING
        );
      default:
        return false;
    }
  }
  switch (type) {
    case 'DPE':
    case 'AMIANTE':
    case 'RISQUES':
    case 'BRUIT':
    case 'NOTICE':
      return true;
    case 'ELEC':
    case 'GAZ':
      // Sans année : on ne peut pas exonérer → exigible (précaution).
      return (
        anneeConstruction == null ||
        new Date().getFullYear() - anneeConstruction >
          DOCUMENT_TECHNIQUE_SEUIL_AGE_ELEC_GAZ_ANS
      );
    case 'PLOMB':
      return (
        anneeConstruction == null ||
        anneeConstruction < DOCUMENT_TECHNIQUE_SEUIL_ANNEE_PLOMB
      );
    default:
      return false;
  }
}

/** Motif i18n quand le type n'est pas exigible. */
export function nonApplicableReason(
  type: DocumentTechniqueType,
  anneeConstruction?: number | null,
  typeUnite?: string | null,
  chauffage?: string | null
): DocumentTechniqueNonApplicableReason | undefined {
  if (isDocumentTechniqueExigible(type, anneeConstruction, typeUnite, chauffage)) {
    return undefined;
  }
  if (isParking(typeUnite)) {
    return type === 'AMIANTE' ? 'amianteParkingTropRecent' : 'parking';
  }
  if (type === 'ELEC' || type === 'GAZ') {
    return 'elecGazTropRecent';
  }
  if (type === 'PLOMB') {
    return 'plombTropRecent';
  }
  return undefined;
}

/** Statut d'affichage d'un document actif (manquant / à jour / expiré / illimité). */
export function statutDocumentActif(
  doc?: LogementDocumentTechnique | null,
  today: Date = new Date()
): DocumentTechniqueStatut {
  if (!doc || doc.actifDto === false) {
    return 'manquant';
  }
  const expiration = toDateIso(doc.dateExpirationDto);
  if (!expiration) {
    return 'illimite';
  }
  return expiration < toDateIso(today) ? 'expire' : 'a_jour';
}

/** Une ligne exigible est OK si document à jour ou sans expiration. */
export function isLigneDossierTechniqueOk(ligne: DocumentTechniqueLigne): boolean {
  if (!ligne.exigible) {
    return true;
  }
  return ligne.statut === 'a_jour' || ligne.statut === 'illimite';
}

/**
 * True si tous les documents exigibles du logement sont à jour
 * (ou sans expiration). Les types non applicables sont ignorés.
 */
export function isDossierTechniqueComplet(
  documents: LogementDocumentTechnique[] = [],
  anneeConstruction?: number | null,
  typeUnite?: string | null,
  chauffage?: string | null,
  today: Date = new Date()
): boolean {
  return lignesDossierTechnique(
    documents,
    anneeConstruction,
    typeUnite,
    chauffage,
    today
  ).every(isLigneDossierTechniqueOk);
}

/** Une ligne par type réglementaire, document actif le plus récent si plusieurs.
 * Pour un parking : uniquement les types exigibles (pas de lignes grisées). */
export function lignesDossierTechnique(
  documents: LogementDocumentTechnique[] = [],
  anneeConstruction?: number | null,
  typeUnite?: string | null,
  chauffage?: string | null,
  today: Date = new Date()
): DocumentTechniqueLigne[] {
  const lignes = DOCUMENT_TECHNIQUE_TYPES.map((type) => {
    const document = documents.find(
      (item) => item.typeDto === type && item.actifDto !== false
    );
    const exigible = isDocumentTechniqueExigible(
      type,
      anneeConstruction,
      typeUnite,
      chauffage
    );
    const statut =
      !exigible && !document
        ? 'non_applicable'
        : statutDocumentActif(document, today);
    return {
      type,
      document,
      statut,
      exigible,
      nonApplicableReason: exigible
        ? undefined
        : nonApplicableReason(type, anneeConstruction, typeUnite, chauffage),
    };
  });
  if (isParking(typeUnite)) {
    return lignes.filter((ligne) => ligne.exigible);
  }
  return lignes;
}
